import { AggregationFunction } from "@multiweb/clients/dataReader";
import { Event as TEvent } from "../../../../Global/Base/Events";
import { NumberUtils } from "../../../../Global/Base/Utils/NumberUtils";
import { EnumType } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { ChartType } from "../../../UI.Controls/ChartElement";
import { IChartColumnDefinition } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuComboBoxItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuComboBoxItem";
import { DropDownMenuInputItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuInputItem";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { DropDownRadioButtons } from "../../../UI.Controls/DropDownMenu/DropDownRadioButtons";
import { SeparatorMenuItem } from "../../../UI.Controls/DropDownMenu/SeparatorItem";
import { SeparatorMenuItemWithText } from "../../../UI.Controls/DropDownMenu/SeparatorItemWithText";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { MWColors } from "../XGControls/Colors";
import { ChartColumnDefinition } from "../XGControls/Frames/ChartSettingsManager";
import { IMasterableFrameWithDataGrid } from "../XGControls/Frames/FrameInterfaces";
/**
 * Představuje záznam svislé osy
 * sestává z barevné reprezentace záznamu a názvu záznamu
 * po kliku na název záznamu se otevře _dropDownMenu
 * _dropDownMenu obsahuje:
 * - _menuEdit - název záznamu
 * - _menuComboBox - atribut svislé osy
 * - _radioButton - možnost volby agregační funkce
 * - tlačítko pro smazání záznamu svislé osy
 */
export class ChartItemRow {
    /** událost změny agregační funkce */
    public aggregationFunctionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny názvu kategorie */
    public captionChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost změny atributu osy y */
    public attrNameChanged: TEvent<string[], void> = new TEvent<string[], void>();
    /** událost kliku na tlačítko smazat */
    public deleteDatasetClicked: TEvent<string[], void> = new TEvent<string[], void>();
    private _colorBox: HTMLElement;
    private _chartType: ChartType;
    public set chartType(type: ChartType) {
        this._chartType = type;
        this._setRowByChartType();
    }
    private _listenerClick: EventListener;
    private _categoryNum: number;
    private _dataSet: ChartColumnDefinition;
    private _masterFrame: IMasterableFrameWithDataGrid;
    private _newElement: HTMLElement;
    /** element zobrazené položky */
    public get item(): HTMLElement {
        return this._newElement;
    }
    private _cssClass: string = "chart-item-row";

    /** zobrazí se po kliku na záznam svislé osy */
    private _dropDownMenu: DropDownMenu;
    /** název záznamu svislé osy pro graf */
    private _menuEdit: DropDownMenuInputItem;
    /** kombobox umožňující výběr atributu svislé osy */
    private _menuComboBox: DropDownMenuComboBoxItem;
    /** data pro zobrazení v _menuComboBox */
    private _comboBoxData: EnumType;
    /** umožňuje volit právě jednu hodnotu v sekci Hodnota v _dropdownmenu */
    private _radioButton: DropDownRadioButtons;

    private _isDisposed: boolean;
    private _isInitialized: boolean;

    public get isDisposed(): boolean {
        return this._isDisposed;
    }

    public element: HTMLElement;

    private _chartColors: MWColors = MWColors.getInstance();

    constructor(comboBoxData: EnumType, dataset: IChartColumnDefinition, masterFrame: IMasterableFrameWithDataGrid, chartType: ChartType) {
        this._categoryNum = +dataset.id;
        this._dataSet = dataset;
        this._masterFrame = masterFrame;
        this._comboBoxData = comboBoxData;
        this._isInitialized = false;
        this._isDisposed = false;
        this._chartType = chartType;
    }

    /**
     * inicializace je zde z důvodu navázání eventů
     * obsah by jinak mohl být v konstruktoru
     */
    public initialize(): void {
        if (this._isInitialized)
            return;
        this._isInitialized = true;
        this._create();
        this._disableOrEnableRadioButtons(this._dataSet.attrName);
        this._setRowByChartType();
    }

    /**
     * otevře dropdownmenu
     * @param openDropDownMenuEvent Událost, na základě které se dropdownmenu otevírá
     */
    public openDropDownMenu(elem: HTMLElement, openDropDownMenuEvent: Event): void {
        this._dropDownMenu.show(elem, openDropDownMenuEvent);
    }

    /**
     * vytvoří záznam svislé osy
     */
    private _create(): void {
        let color = "";
        if (this._dataSet.chartColor) {
            color = this._dataSet.chartColor;
        } else {
            const colorIndex = this._categoryNum % this._chartColors.primaryColors.length;
            color = this._chartColors.primaryColors[colorIndex].className;
            this._dataSet.chartColor = color;
        }

        this.element = createElement({ classes: [this._cssClass] });

        const newLeftPart = createElement({ classes: [this._cssClass + "__cell"] });

        this._colorBox = createElement({ classes: [this._cssClass + "__color-box"], css: { background: this._chartColors.getThemeColor(color) } });
        this._newElement = createElement({ type: "button", classes: [this._cssClass + "__item"] });
        if (this._dataSet.caption) {
            this._newElement.textContent = this._dataSet.caption;
        } else {
            Localization.getLocalizationItem("GUI-232972").then((locItem) => { //kategorie
                this._newElement.textContent = locItem.primaryText + " " + (this._categoryNum + 1);
                this._dataSet.caption = locItem.primaryText + " " + (this._categoryNum + 1);
            });
        }
        newLeftPart.appendChild(this._colorBox);
        newLeftPart.appendChild(this._newElement);

        const newRightPart = createElement({ classes: [this._cssClass + "__filter"] });
        const newMenu = createElement({ html: "<div class=\"Icon menuIcon ui-filter\"></div>" });
        newRightPart.appendChild(newMenu);

        this.element.appendChild(newLeftPart);
        this.element.appendChild(newRightPart);

        /** vytvoreni dropdown menu */
        this._dropDownMenu = new DropDownMenu();

        /** vlozeni inputu do dropdownmenu */
        this._menuEdit = new DropDownMenuInputItem(null, null);
        if (this._dataSet.caption) {
            this._menuEdit.editField.value = this._dataSet.caption;
        } else {
            Localization.getLocalizationItem("GUI-232972").then((locItem) => { //kategorie
                this._menuEdit.editField.value = locItem.primaryText + " " + (this._categoryNum + 1);
                this._dataSet.caption = this._menuEdit.editField.value;
            });
        }
        this._menuEdit.noCloseAfterSelect = true;
        this._menuEdit.priority = 3;
        this._dropDownMenu.addItem(this._menuEdit);
        this._menuEdit.inputChanged.add(value => this._runChartSettingsManagerEvents(value, this.captionChanged));

        /** vlozeni comboboxu do dropdownmenu */
        this._menuComboBox = DropDownMenuComboBoxItem.createDropDownMenuComboBoxItem(this._comboBoxData, null);
        this._menuComboBox.noCloseAfterSelect = true;
        this._menuComboBox.initializeCombo(this._dataSet.attrName);
        this._dropDownMenu.addItem(this._menuComboBox);
        this._menuComboBox.comboBox.valueChanged.add(() => this._runChartSettingsManagerEvents(this._menuComboBox.comboBox.getSelectedItemValue(), this.attrNameChanged));

        this._dropDownMenu.addItem(SeparatorMenuItemWithText.createSeparatorWithText("GUI-328583")); //hodnota

        /**
         * vlozeni tzv radiobuttonu do dropdownmenu
         * počet, součet, průměr, minimum, maximum
         */
        this._radioButton = DropDownRadioButtons.createDropDownRadioButtons([["GUI-310038", AggregationFunction.count], ["GUI-310040", AggregationFunction.sum], ["GUI-310044", AggregationFunction.avg], ["GUI-310046", AggregationFunction.min], ["GUI-310048", AggregationFunction.max]], this._dropDownMenu, null);
        if (this._dataSet.aggregateFunction) {
            this._radioButton.selectRadioButton(this._dataSet.aggregateFunction, false);
        } else {
            this.aggregationFunctionChanged.trigger([AggregationFunction.count, this._categoryNum + ""], null);
        }
        this._radioButton.radioButtonSelectionChanged.add(value => this._runChartSettingsManagerEvents(value, this.aggregationFunctionChanged));

        this._dropDownMenu.addItem(new SeparatorMenuItem());

        this._dropDownMenu.addItem(DropDownMenuItem.createDropMenuItem("editorial-trash", async () => this._runChartSettingsManagerEvents("", this.deleteDatasetClicked), "GUI-26", true, 3)); //smazat

        //klik na atribut svisle osy
        this._newElement.addEventListener("click", this._listenerClick = (e: Event) => {
            this._dropDownMenu.show(this.item, e);
        });
    }

    private _runChartSettingsManagerEvents(value: string, chartSettingsManagerEvent: TEvent<string[], void>): void {
        switch (chartSettingsManagerEvent) {
            case this.captionChanged:
                this._newElement.textContent = value;
                break;
            case this.attrNameChanged:
                if (value == null)
                    value = "ID";
                this._disableOrEnableRadioButtons(value);
                break;
            case this.deleteDatasetClicked:
                this.dispose();
                this.element.remove();
                break;
        }
        chartSettingsManagerEvent.trigger([value, this._categoryNum + ""], null);
    }

    /**
     * je-li zvolená hodnota _menuComboBox jiná než číselná, povolí se pouze hodnota count v _radioButton
     * výběr promítne do chartsettingsmanageru
     * @param selectedValue Vybraná hodnota _menuComboBox
     * @param trigger Je-li true, vyvolá změnu v chartsettingsmanageru
     */
    private _disableOrEnableRadioButtons(selectedValue: string, trigger: boolean = true): void {
        const selectedDataGrid = this._masterFrame.currentDataGrid.columns.find(item => item.attrName == selectedValue);

        if (selectedValue == "ID" || (selectedDataGrid && !NumberUtils.isNumberType(selectedDataGrid.dataType))) {
            this._radioButton.disableOtherButtonsThen(AggregationFunction.count);
            this._radioButton.selectRadioButton(AggregationFunction.count, trigger);
        } else {
            this._radioButton.enableAllButtons();
        }
    }

    /**
     * Provede nastavení řádku na základě typu grafu
     * @param chartType Typ grafu
     */
    private _setRowByChartType(): void {
        if (!this._colorBox)
            return;

        switch (this._chartType) {
            case ChartType.pie:
            case ChartType.doughnut:
                this._colorBox.style.setProperty("display", "none");
                break;
            default:
                this._colorBox.style.setProperty("display", "block");
        }
    }

    public dispose(): void {
        this._newElement.removeEventListener("click", this._listenerClick);

        this._radioButton.finalyDispose();
        this._radioButton.dispose();
        this._menuComboBox.finallyDispose();
        this._menuComboBox.dispose();
        this._menuEdit.finallyDispose();
        this._menuEdit.dispose();
        this._dropDownMenu.close();
        this._dropDownMenu.dispose();
        this._isDisposed = true;
    }
}
